import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
} from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SettingsService } from '../settings.service';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { ManageOrganizationDrawerParams } from '../../manage-organization.interface';
import { DrawerService } from '../../../../core/services/drawer.service';
import { OrgManagementMode } from '../../manage-organization.enums';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { ApiRespones } from '../settings.interface';
import { GENERAL_OPTION_IDS, DEVELOPMENT_AREA_IDS } from './options.enums';
import { CommonService } from '../../../../shared/services/common.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-options',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzCheckboxModule,
    NzButtonModule,
  ],
  templateUrl: './options.component.html',
  styleUrls: ['./options.component.scss'],
})
export class OptionsComponent implements OnInit, OnDestroy {
  optionsForm!: FormGroup;
  selectedOptions = [];
  loading = false;
  options: number[] = [];
  isUpdating = false;
  private readonly destroy$ = new Subject<void>();
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
  };
  errorMessage = '';

  allOptions: { id: number; value: string }[] = [];
  generalOptions: { id: number; label: string }[] = [];
  developmentAreas: { id: number; label: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private settingService: SettingsService,
    private drawerService: DrawerService,
    private cdr: ChangeDetectorRef,
    private commonService: CommonService,
    private route: ActivatedRoute
  ) {
    this.setupDrawerParams();
  }

  get isEditMode(): boolean {
    return this.orgParams.mode === OrgManagementMode.EDIT;
  }

  ngOnInit(): void {
    this.fetchOptionsAndInitializeForm();
  }

  private fetchOptionsAndInitializeForm(): void {
    this.loading = true;

    this.settingService
      .getOptionsLookup()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: options => {
          this.allOptions = options;

          this.generalOptions = options
            .filter(option => GENERAL_OPTION_IDS.includes(option.id))
            .map(option => ({ id: option.id, label: option.value }));

          this.developmentAreas = options
            .filter(option => DEVELOPMENT_AREA_IDS.includes(option.id))
            .map(option => ({ id: option.id, label: option.value }));

          this.initializeForm();
          this.loadData();
        },
        error: error => this.handleError(error),
      });
  }

  private initializeForm(): void {
    this.optionsForm = this.fb.group({
      generalOptions: this.fb.group(
        this.createFormControls(this.generalOptions)
      ),
      developmentAreas: this.fb.group(
        this.createFormControls(this.developmentAreas)
      ),
    });
  }

  private createFormControls(items: { id: number; label: string }[]) {
    const controls: Record<number, FormControl> = {};
    items.forEach(item => {
      controls[item.id] = new FormControl(false);
    });
    return controls;
  }

  loadData(): void {
    this.loading = true;

    this.settingService
      .getOptions(this.orgParams.organizationId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data: ApiRespones<number>) => {
          this.options = data.result.optionIds;
          this.setSelectedValues();
        },
        error: error => this.handleError(error),
      });
  }

  setSelectedValues(): void {
    const generalOptionsGroup = this.optionsForm.get(
      'generalOptions'
    ) as FormGroup;
    const developmentAreasGroup = this.optionsForm.get(
      'developmentAreas'
    ) as FormGroup;

    Object.keys(generalOptionsGroup.controls).forEach(key => {
      generalOptionsGroup.get(key)?.setValue(this.options.includes(+key));
    });

    Object.keys(developmentAreasGroup.controls).forEach(key => {
      developmentAreasGroup.get(key)?.setValue(this.options.includes(+key));
    });
  }

  getSelectedIds(): number[] {
    const selectedIds: number[] = [];

    const generalOptionsGroup = this.optionsForm.get(
      'generalOptions'
    ) as FormGroup;
    Object.keys(generalOptionsGroup.controls).forEach(key => {
      const control = generalOptionsGroup.get(key);
      if (control?.value) {
        selectedIds.push(+key);
      }
    });

    const developmentAreasGroup = this.optionsForm.get(
      'developmentAreas'
    ) as FormGroup;
    Object.keys(developmentAreasGroup.controls).forEach(key => {
      const control = developmentAreasGroup.get(key);
      if (control?.value) {
        selectedIds.push(+key);
      }
    });

    return selectedIds;
  }

  private handleError(error: Error): void {
    this.loading = false;
    this.isUpdating = false;
    this.commonService.notify('error', error.message);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  onSubmit(): void {
    const payload = {
      organizationId: this.orgParams.organizationId,
      optionIds: this.getSelectedIds(),
    };

    this.isUpdating = true;
    const operation$ = this.settingService.updateOptions(payload);
    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isUpdating = false;
        })
      )
      .subscribe({
        next: () => {
          this.orgParams.mode = OrgManagementMode.VIEW;

          this.commonService.notify('success', `Options updated successfully`);
          this.loadData();
        },
        error: error => this.handleError(error),
      });
  }

  enableEdit() {
    this.orgParams.mode = OrgManagementMode.EDIT;
  }
  onCancel() {
    this.orgParams.mode = OrgManagementMode.VIEW;
    this.loadData();
  }
}
